import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog.jsx';
import { Search, ArrowRight, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useV3Enabled } from '@/lib/v3-flag.js';
import { searchPipeline } from '@/services/search.service.js';
import { cn } from '@/lib/utils.js';
import { paletteOwner, V2_PALETTE } from '@/lib/palette-owner.js';
import { flattenRows, hrefForRow, INLINE_TERMS } from '@/lib/search-rows.js';

// cmd-K palette (spec §6.3). Lightweight build: Dialog + Input +
// keyboard-navigated result list. Opens on cmd-K / ctrl-K, on the
// 'search-palette:open' window event, or via the mobile search icon
// in the AppShell. Debounced 200ms. With the v3 flag on the V3 palette owns
// Cmd+K everywhere and this one registers nothing (lib/palette-owner.js).
//
// CLIENT-RECORD-SPEC §4: a result is the CLIENT, not four versions of him.
// The server returns one `type: 'account'` row per insured with its terms
// nested current-first (plus `type: 'deal'` rows for hits with no account).
// The first INLINE_TERMS terms render under the account; "+n more" expands
// the rest. Keyboard navigation walks accounts and their visible terms as
// one flat list, so Enter on an account lands on the client record and Enter
// on a term lands on that deal.

// The rows and each row's link are lib/search-rows.js, shared with the /search page (one derivation, 2026-11-02).
export { flattenRows, hrefForRow };

export default function SearchPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [expanded, setExpanded] = useState(() => new Set());
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const owner = paletteOwner(useV3Enabled());

  // Open on cmd-K / ctrl-K and on the search-palette:open custom event.
  useEffect(() => {
    if (owner !== V2_PALETTE) return undefined;
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    function onCustom() {
      setOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('search-palette:open', onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('search-palette:open', onCustom);
    };
  }, [owner]);

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setQ('');
      setResults([]);
      setActiveIdx(0);
      setExpanded(new Set());
    } else {
      // Focus the input after the dialog content mounts.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const r = await searchPipeline(q);
        setResults(r?.results || []);
        setActiveIdx(0);
        setExpanded(new Set());
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const rows = useMemo(() => flattenRows(results, expanded), [results, expanded]);

  function selectRow(row) {
    const href = hrefForRow(row);
    if (!href) return;
    setOpen(false);
    navigate(href);
  }

  function toggleExpanded(accountId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId); else next.add(accountId);
      return next;
    });
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight') {
      // Expand the active account's remaining terms without leaving the keyboard.
      const row = rows[activeIdx];
      if (row?.kind === 'account' && (row.r.terms || []).length > INLINE_TERMS && !expanded.has(row.r.id)) {
        e.preventDefault();
        toggleExpanded(row.r.id);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectRow(rows[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const lineBadge = (r) =>
    r.line && r.line !== 'Aviation' ? (
      <span className="shrink-0 rounded border border-border px-1 text-[9px] uppercase tracking-wide text-text-dim">
        {r.line}
      </span>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0">
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search size={16} strokeWidth={1.5} className="text-text-dim" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search accounts, deals, app #, N-numbers…"
            className="h-12 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-dim"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          <kbd className="hidden font-mono text-[10px] text-text-dim md:inline">
            esc to close
          </kbd>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto py-1">
          {loading ? (
            <li className="px-3 py-4 text-center text-xs text-text-dim">Searching…</li>
          ) : rows.length === 0 ? (
            <li className="px-3 py-4 text-center text-xs text-text-dim">
              {q.trim().length < 2
                ? 'Type at least 2 characters'
                : 'No matches in the active pipeline.'}
            </li>
          ) : (
            rows.map((row, i) => {
              const isTerm = row.kind === 'term';
              const r = isTerm ? row.t : row.r;
              const hidden = row.kind === 'account' ? Math.max((row.r.terms || []).length - INLINE_TERMS, 0) : 0;
              const showMore = row.kind === 'account' && hidden > 0 && !expanded.has(row.r.id);
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={() => selectRow(row)}
                    onMouseEnter={() => setActiveIdx(i)}
                    data-search-row={row.kind}
                    data-search-href={hrefForRow(row) || ''}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 py-2 text-left',
                      isTerm ? 'pl-8 pr-3' : 'px-3',
                      activeIdx === i ? 'bg-accent-soft' : 'hover:bg-border/30'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {isTerm ? <ChevronRight size={12} strokeWidth={1.5} className="shrink-0 text-text-dim" /> : null}
                        <span className={cn('truncate', isTerm ? 'text-[13px] text-text' : 'text-sm font-medium text-text')}>{r.label}</span>
                        {/* Line badge (Ship 4 item 8): non-Aviation rows carry
                            their line so twin-account results read apart. */}
                        {lineBadge(r)}
                      </div>
                      {r.sublabel ? (
                        <div className="truncate text-[11px] text-text-dim">{r.sublabel}</div>
                      ) : null}
                    </div>
                    <ArrowRight
                      size={14}
                      strokeWidth={1.5}
                      className={cn(
                        'shrink-0 transition',
                        activeIdx === i ? 'text-accent' : 'text-text-dim'
                      )}
                    />
                  </button>
                  {showMore ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(row.r.id); }}
                      className="w-full py-1 pl-8 pr-3 text-left text-[11px] text-text-dim hover:text-text"
                    >
                      +{hidden} more term{hidden === 1 ? '' : 's'}
                    </button>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
